import {
  FileText,
  ExternalLink,
  Target,
  Waypoints,
  Server,
  GitBranch,
  Clock,
  Trash2,
  ArrowRight,
} from 'lucide-react';
import { Badge, Button, Card, CardBody } from '../../ui';
import {
  docTypeLabel,
} from '../../features/documents/documentConfig';
import {
  formatDate,
  statusLabel,
  statusTone,
} from '../../features/documents/documentModel';
import {
  assessFreshness,
  freshnessLabel,
  freshnessNeedsAttention,
  freshnessTone,
} from '../../features/intelligence/freshness';
import type { TeamDocumentSummary } from '../../features/documents/types';

export interface DocumentCardProps {
  doc: TeamDocumentSummary;
  goalTitle?: string;
  workstreamTitle?: string;
  serviceTitles: Map<string, string>;
  onOpen?: (id: string) => void;
  onDelete?: (doc: TeamDocumentSummary) => void;
}

/** A single Document Bank card — metadata + relationships + the external link. */
export function DocumentCard({
  doc,
  goalTitle,
  workstreamTitle,
  serviceTitles,
  onOpen,
  onDelete,
}: DocumentCardProps) {
  const fresh = assessFreshness(doc);

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="neutral">
              <FileText className="h-3 w-3" /> {docTypeLabel(doc.docType)}
            </Badge>
            <Badge tone={statusTone(doc.status)} dot>
              {statusLabel(doc.status)}
            </Badge>
            {freshnessNeedsAttention(fresh.state) && (
              <Badge tone={freshnessTone(fresh.state)} title={fresh.reason}>
                <Clock className="h-3 w-3" /> {freshnessLabel(fresh.state)}
                {fresh.ageDays !== undefined && fresh.state !== 'SUPERSEDED'
                  ? ` · ${fresh.ageDays}d`
                  : ''}
              </Badge>
            )}
          </div>
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(doc)}
              aria-label="Delete document"
              className="shrink-0 rounded-md p-1 text-zinc-500 transition-colors hover:bg-surface-3 hover:text-red-300"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={() => onOpen?.(doc.id)}
          disabled={!onOpen}
          className="group flex w-full items-start gap-2 text-left"
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-zinc-100 group-hover:text-white">
              {doc.title || 'Untitled document'}
            </span>
            {doc.summary && (
              <span className="mt-0.5 line-clamp-2 block text-xs text-zinc-400">
                {doc.summary}
              </span>
            )}
          </span>
          {onOpen && (
            <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-zinc-600 transition-colors group-hover:text-zinc-300" />
          )}
        </button>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
          {doc.ownerPoc && (
            <span>
              Owner: <span className="text-zinc-300">{doc.ownerPoc}</span>
            </span>
          )}
          {doc.authors.length > 0 && (
            <span>
              Authors:{' '}
              <span className="text-zinc-300">{doc.authors.join(', ')}</span>
            </span>
          )}
          {doc.createdDate && <span>{formatDate(doc.createdDate)}</span>}
        </div>

        {/* Relationship chips. */}
        <div className="flex flex-wrap items-center gap-1.5">
          {doc.relatedGoalId && (
            <Badge tone="accent">
              <Target className="h-3 w-3" /> {goalTitle ?? 'Goal'}
            </Badge>
          )}
          {doc.relatedWorkstreamId && (
            <Badge tone="info">
              <Waypoints className="h-3 w-3" /> {workstreamTitle ?? 'Workstream'}
            </Badge>
          )}
          {doc.relatedServices.map((s) => (
            <Badge key={s} tone="neutral">
              <Server className="h-3 w-3" /> {serviceTitles.get(s) ?? s}
            </Badge>
          ))}
          {doc.relatedRepo && (
            <Badge tone="neutral">
              <GitBranch className="h-3 w-3" /> {doc.relatedRepo}
            </Badge>
          )}
          {doc.tags.map((t) => (
            <Badge key={t} tone="neutral">
              #{t}
            </Badge>
          ))}
        </div>

        <div className="flex items-center gap-2 pt-1">
          {doc.documentLink && (
            <a href={doc.documentLink} target="_blank" rel="noreferrer">
              <Button
                size="sm"
                variant="secondary"
                leftIcon={<ExternalLink className="h-3.5 w-3.5" />}
              >
                Open document
              </Button>
            </a>
          )}
          {onOpen && (
            <Button size="sm" variant="ghost" onClick={() => onOpen(doc.id)}>
              Details
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

export default DocumentCard;
